<script setup lang="ts">
import { useUserMe } from "~/components/user/composables/useUser";
import { useApi } from "~/composables/useApi";
import { useToast } from "~/composables/useToast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AwButton, AwIcon, AwTooltip } from "@asap/design";
import { canSyncTeamMemberData, getNextTeamMemberDataSyncDate } from "@asap/shared/utils/team-member-data-sync";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    lastSyncedAt?: string | null;
    teamMemberId?: string;
  }>(),
  { disabled: false, lastSyncedAt: null, teamMemberId: undefined }
);

const emit = defineEmits<{
  synced: [lastSyncedAt: string];
}>();

const { invoke } = useApi();
const { teamMember, isDeveloper } = useUserMe();
const { addToastError, addToastSuccess } = useToast();

const isLoading = ref(false);

const teamMemberId = computed(() => props.teamMemberId ?? teamMember.value?.id);

const canSync = computed(() => isDeveloper.value || canSyncTeamMemberData(props.lastSyncedAt));

const syncDisabledTooltip = computed(() => {
  if (canSync.value || props.disabled) {
    return null;
  }

  if (props.lastSyncedAt === null) {
    return null;
  }

  const nextSyncDate = getNextTeamMemberDataSyncDate(props.lastSyncedAt);
  const nextSyncDateLabel = format(nextSyncDate, "d MMMM yyyy 'à' HH:mm", { locale: fr });

  return `La prochaine synchronisation sera possible à partir du ${nextSyncDateLabel}`;
});

const syncTeamMemberData = async () => {
  if (!teamMemberId.value) return;

  try {
    isLoading.value = true;
    const response = await invoke({
      path: "team-member-data",
      method: "POST",
      body: {
        teamMemberIds: [teamMemberId.value],
        type: "consultant_kpi",
      },
    });

    if (response.success) {
      const syncedAt = response.data.items.at(0)?.created_at;
      addToastSuccess({ description: "Les KPI ont bien été synchronisés" });
      if (syncedAt) {
        emit("synced", syncedAt);
      }
    } else {
      addToastError({ title: "Une erreur est survenue lors de la synchronisation" }, response.error.message);
    }
  } finally {
    isLoading.value = false;
  }
};

const ui = {
  button: "shrink-0",
};
</script>

<template>
  <div v-if="teamMemberId" v-bind="$attrs">
    <AwTooltip v-if="syncDisabledTooltip" :text="syncDisabledTooltip" variant="white" :max-width="350">
      <AwButton size="md" color="secondary" :class="ui.button" :disabled="true" :is-loading="isLoading">
        <AwIcon name="i-ph-arrows-clockwise-duotone" class="size-3.5" />
        Synchroniser
      </AwButton>
    </AwTooltip>
    <AwButton
      v-else
      size="md"
      color="secondary"
      :class="ui.button"
      variant="outline"
      :disabled="disabled"
      :is-loading="isLoading"
      @click="syncTeamMemberData"
    >
      <AwIcon name="i-ph-arrows-clockwise-duotone" class="size-3.5" />
      Synchroniser
    </AwButton>
  </div>
</template>
